import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should an Indonesian invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A standard Indonesian invoice should include: your business name and address; your NPWP (Nomor Pokok Wajib Pajak — tax ID) if applicable; the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount in IDR; and your bank details. PKP-registered businesses (taxable entrepreneurs) must issue a Faktur Pajak (tax invoice) that includes the PPN rate (11%), PPN amount, and total inclusive of PPN.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is PPN in Indonesia and what is the rate?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'PPN (Pajak Pertambahan Nilai) is Indonesia\'s Value Added Tax. The standard PPN rate is 11% (increased from 10% in April 2022). Businesses must register as PKP (Pengusaha Kena Pajak — taxable entrepreneur) with the DJP (Directorate General of Taxes) once annual taxable turnover exceeds IDR 4.8 billion. Registered PKPs issue Faktur Pajak electronically via the DJP\'s e-Faktur system.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Indonesian freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Indonesian freelancers providing services to overseas clients typically invoice in USD. The export of services to foreign clients is zero-rated for PPN (0% PPN), meaning no PPN is charged on international invoices. Most Indonesian freelancers receive international payments via Payoneer, Wise, or bank wire transfer (SWIFT). PayPal is also available in Indonesia. Always invoice in the currency agreed with your client.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is a Faktur Pajak in Indonesia?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Faktur Pajak is a tax invoice issued by PKP-registered businesses in Indonesia. It must be generated electronically through the DJP\'s e-Faktur application and transmitted to DJP servers in real time. Each Faktur Pajak has a unique 16-digit tax invoice series number. PKPs who fail to issue Faktur Pajak for taxable supplies face penalties. For businesses below the PKP threshold, a standard invoice (not Faktur Pajak) is sufficient.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Indonesian users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Indonesian freelancers and businesses. Create professional invoices in IDR, USD, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month. For Faktur Pajak (e-Faktur) obligations, you will need to use the DJP\'s official e-Faktur system.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'PPN Invoice',
    subtitle: 'For PKP-registered Indonesian businesses',
    features: ['11% PPN auto-calculated', 'NPWP / PKP number field', 'Net + PPN + total', 'DJP-ready format', 'IDR currency'],
    to: '/create/vat',
    color: 'red',
    btnClass: 'bg-red-600 hover:bg-red-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below IDR 4.8B threshold',
    features: ['No PPN required', 'IDR currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'slate',
    btnClass: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP, EUR billing', 'Zero-rated export format', 'SWIFT / Payoneer details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorIndonesiaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Indonesia: Free IDR Invoice Maker | PPN 11% | Invilic"
        description="Free invoice generator for Indonesian freelancers and businesses. Create PPN (VAT) invoices with 11% PPN or standard IDR invoices. PDF download free, no sign-up required."
        canonical="https://www.invilic.com/invoice-generator-indonesia"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇮🇩 Indonesia
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Indonesia
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Indonesian businesses and freelancers. Create PPN invoices with 11% PPN auto-calculated, or standard IDR invoices. Download professional PDF invoices instantly — no sign-up needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200">
              Create PPN Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">PPN Invoice vs Standard Invoice in Indonesia</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-red-700 dark:text-red-400 mb-3">Use a PPN Invoice if:</div>
              {[
                'Your annual taxable turnover exceeds IDR 4.8 billion',
                'You are registered as PKP with the DJP (Directorate General of Taxes)',
                'Your client needs a Faktur Pajak for their input tax credit claim',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your annual taxable turnover is below IDR 4.8 billion',
                'You are a freelancer or individual not registered as PKP',
                'You are billing an overseas client (export — no PPN charged)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16 p-6 rounded-2xl border border-red-100 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Receiving International Payments as an Indonesian Freelancer</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Indonesia has a large and growing freelance economy in IT, design, and content creation. Key payment options:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: 'Payoneer', detail: 'Most popular for Indonesian freelancers on Upwork, Fiverr, and Freelancer. Withdraw to BCA, BNI, Mandiri, or other Indonesian banks in IDR.' },
              { label: 'Wise', detail: 'Receive USD, EUR, or GBP at the mid-market rate and convert to IDR. Low fees compared to traditional SWIFT transfers.' },
              { label: 'Bank wire (SWIFT)', detail: 'Provide your Indonesian bank SWIFT code and account number. BCA (CENAIDJA), Bank Mandiri (BMRIIDJA), BNI (BNINIDJA) all support international SWIFT transfers.' },
              { label: 'GoPay / OVO / DANA', detail: 'Domestic digital wallets for local Indonesian clients. For international clients, Payoneer or Wise is preferred.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-red-100 dark:border-red-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Indonesia Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Indonesian Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Indonesian businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
